import axios from 'axios';
import { setupAuthInterceptors } from './authInterceptor';
import { extractErrorMessage } from '../utils/errorHandler';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://accurix-backend-zq4b.onrender.com/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

setupAuthInterceptors(apiClient);



export const getCategoryBreakdown = async (companyId) => {
  try {
    const response = await apiClient.get(`/analytics/transactions/by-category/${companyId}/`);
    
    // Sort categories by total_amount in descending order
    const sortedCategories = [...response.data.categories].sort((a, b) => b.total_amount - a.total_amount);
    
    // Get top 4 categories and group the rest as "Others"
    const topCategories = sortedCategories.slice(0, 4);
    const restCategories = sortedCategories.slice(4);
    
    const labels = topCategories.map(cat => cat.name);
    const values = topCategories.map(cat => cat.total_amount);
    
    // Sum up the rest and add as "Others" if there are remaining categories
    if (restCategories.length > 0) {
      const othersAmount = restCategories.reduce((sum, cat) => sum + cat.total_amount, 0);
      labels.push('Others');
      values.push(othersAmount);
    }
    
    return {
      company_id: response.data.company_id,
      company_name: response.data.company_name,
      labels,
      values,
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

export const getTransactionCount = async (companyId) => {
  try {
    const response = await apiClient.get(`/analytics/transactions/count/${companyId}/`);
    
    return {
      company_id: response.data.company_id,
      company_name: response.data.company_name,
      total_transactions: response.data.total_transactions || 0,
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

export const getTransactionSummary = async (companyId) => {
  try {
    const response = await apiClient.get(`/analytics/transactions/summary/${companyId}/`);
    
    return {
      company_id: response.data.company_id,
      company_name: response.data.company_name,
      income: response.data.income || 0,
      expenses: response.data.expenses || 0,
      profit_loss: response.data.profit_loss || 0,
      // Keep old field names for backward compatibility
      total_income: response.data.income || response.data.total_income || 0,
      total_expense: response.data.expenses || response.data.total_expense || 0,
      net: response.data.profit_loss || response.data.net || 0,
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

export const getChartData = async (companyId) => {
  try {
    const response = await apiClient.get(`/analytics/transactions/chart/${companyId}/`);
    
    // Transform raw data into chart format
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Show all 12 months but with blank paths where there's no data
    const labels = monthNames;
    const incomeValues = new Array(12).fill(0);
    const expenseValues = new Array(12).fill(0);
    
    // Parse the data array and fill in values for months that have data
    if (response.data.data && Array.isArray(response.data.data)) {
      response.data.data.forEach(item => {
        // Convert month string "2026-02" to month index (0-11)
        const monthIndex = parseInt(item.month.split('-')[1]) - 1;
        incomeValues[monthIndex] = item.income || 0;
        expenseValues[monthIndex] = item.expenses || 0;
      });
    }
    
    return {
      company_id: response.data.company_id,
      company_name: response.data.company_name,
      labels,
      datasets: [
        { label: 'Income', values: incomeValues },
        { label: 'Expenses', values: expenseValues },
      ],
    };
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};

/**
 * Get transaction report for a date range
 * @param {string} companyId - Company ID
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<Object>} Report data with transactions and summary
 */
export const getTransactionReport = async (companyId, startDate, endDate) => {
  try {
    const params = {
      start_date: startDate,
      end_date: endDate,
    };
    
    const response = await apiClient.get(`/analytics/transactions/report/${companyId}/`, { params });
    
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error));
  }
};
